import { useCallback, useEffect, useState } from 'react';
import type { GeoDisplayId, GeoDisplaySpec, VariantId, VariantSpec } from './types';

export const VARIANTS: VariantSpec[] = [
  { id: 'current', label: 'Actuel', note: 'Production : sélection, puis recherche, puis 9 outils alignés sans séparation, mesures comprises. Géométries uniquement dans le rail.' },
  { id: 'groupes', label: 'A — Groupes séparés', note: 'Outils d’action séparés par famille (bâtiments / réseaux). Mesures en bas près du 2D/3D.' },
  { id: 'menus', label: 'B — Menus d’outils', note: 'Un menu libellé par famille : les icônes deviennent des boutons lisibles.' },
  { id: 'contextuel', label: 'C — Mode contextuel', note: 'Un sélecteur de famille ; seuls les outils de la famille active sont affichés.' },
];

export const GEO_DISPLAYS: GeoDisplaySpec[] = [
  { id: 'groupe', label: 'G1 — Famille de plus', note: 'Géométries traitée comme Bâtiments et Réseaux : elle suit la variante A/B/C et reste visible sans sélection.' },
  { id: 'menu', label: 'G2 — Menu dédié', note: 'Un seul bouton « Géométries ▾ », toujours au même endroit, quelle que soit la variante.' },
  { id: 'contextuelle', label: 'G3 — Barre contextuelle', note: 'Seul « Créer » reste dans la barre ; Modifier / Découper / Fusionner / Défusionner apparaissent au-dessus de la carte quand un bâtiment est sélectionné.' },
  { id: 'mode', label: 'G4 — Mode édition', note: 'Un interrupteur « Éditer les géométries » : les outils de sélection cèdent la place aux outils de tracé.' },
];

const VARIANT_IDS: readonly string[] = VARIANTS.map((v) => v.id);
const GEO_IDS: readonly string[] = GEO_DISPLAYS.map((g) => g.id);

/** State kept in the URL (`?variant=menus&geo=contextuelle`) so a tester can share the exact screen. */
function useUrlChoice<T extends string>(key: string, ids: readonly string[], fallback: T) {
  const read = useCallback((): T => {
    const value = new URLSearchParams(window.location.search).get(key);
    return value && ids.includes(value) ? (value as T) : fallback;
  }, [key, ids, fallback]);

  const [value, setValue] = useState<T>(read);

  useEffect(() => {
    const onPop = () => setValue(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [read]);

  const change = useCallback((next: T) => {
    setValue(next);
    const url = new URL(window.location.href);
    url.searchParams.set(key, next);
    window.history.replaceState({}, '', url);
  }, [key]);

  return [value, change] as const;
}

export function useVariant() {
  const [variant, setVariant] = useUrlChoice<VariantId>('variant', VARIANT_IDS, 'current');
  return { variant, setVariant };
}

export function useGeoDisplay() {
  const [geo, setGeo] = useUrlChoice<GeoDisplayId>('geo', GEO_IDS, 'groupe');
  return { geo, setGeo };
}
